import "../../global.css";
import { useEffect, useState } from "react";
import { userRequest } from "../../requestMethods";
import { useCompany } from "../../components/context/CompanyContext";
import CustomGrid from "../../components/grid/CustomGrid";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import Swal from "sweetalert2";
import Loading from "../../components/loading/loading";

const EMPTY_FORM = { maxTimes: "", sellerTimes: "" };

export default function CardOperator() {
  const { companyId } = useCompany();
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (companyId > 0) getPermission();
  }, [companyId]);

  const getPermission = async () => {
    try {
      setIsLoading(true);
      const res = await userRequest.get(`${companyId}/payment/permission`);
      setPermission(res.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setForm({
      maxTimes: permission?.maxTimes ?? "",
      sellerTimes: permission?.sellerTimes ?? "",
    });
    setEditing(true);
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditing(false);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    if (val === "" || (/^\d+$/.test(val) && parseInt(val) >= 0)) {
      setForm((prev) => ({ ...prev, [e.target.name]: val }));
    }
  };

  const handleSave = async () => {
    const maxTimes = parseInt(form.maxTimes);
    const sellerTimes = parseInt(form.sellerTimes);

    if (!maxTimes || maxTimes < 1) {
      Swal.fire(
        "Atenção!",
        "Informe o máximo de parcelas (mínimo 1).",
        "warning",
      );
      return;
    }
    if (isNaN(sellerTimes) || sellerTimes < 0) {
      Swal.fire("Atenção!", "Informe as parcelas sem juros.", "warning");
      return;
    }
    if (sellerTimes > maxTimes) {
      Swal.fire(
        "Atenção!",
        "Parcelas sem juros não pode ser maior que o máximo de parcelas.",
        "warning",
      );
      return;
    }

    try {
      setIsLoading(true);
      await userRequest.put(`${companyId}/payment/permission`, {
        maxTimes,
        sellerTimes,
      });
      await getPermission();
      setEditing(false);
      setForm(EMPTY_FORM);
      Swal.fire("Sucesso!", "Configuração salva com sucesso.", "success");
    } catch (e) {
      Swal.fire("Erro!", "Não foi possível salvar.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      field: "maxTimes",
      type: "number",
      headerName: "Máximo de parcelas",
      width: 200,

      align: "center",
      headerAlign: "center",
    },
    {
      field: "sellerTimes",
      type: "number",
      headerName: "Parcelas sem juros",
      width: 200,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "editar",
      type: "text",
      headerName: "",
      width: 60,
      renderCell: () => (
        <IconButton size="small" onClick={handleEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const rows = permission
    ? [{ ...permission, id: permission.taxPaymentPermissionId }]
    : [];

  return (
    <div className="cust-page">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="cust-title">Operadora Cartão</div>
          <p style={{ fontSize: 12, color: "#888", margin: "0 0 12px 0" }}>
            ⚠️ As configurações de parcelamento devem estar de acordo com as condições contratadas junto à operadora do cartão. Valores incorretos podem causar divergências nas cobranças.
          </p>

          {/* Lista */}
          <div className="cust-card">
            <div className="cust-section-header">
              Configuração de Parcelamento
            </div>
            <div style={{ padding: 16 }}>
              {rows.length > 0 ? (
                <CustomGrid
                  gridId="cardOperatorList"
                  rows={rows}
                  columns={columns}
                  pageSize={5}
                  checkboxSelection={false}
                />
              ) : (
                <p style={{ color: "#888", fontSize: 13 }}>
                  Nenhuma configuração cadastrada.
                </p>
              )}
            </div>
            {!editing && rows.length === 0 && (
              <div style={{ padding: "0 16px 16px" }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  Configurar
                </Button>
              </div>
            )}
          </div>

          {/* Formulário de edição */}
          {editing && (
            <div className="cust-card">
              <div className="cust-section-header">Editar Configuração</div>
              <div className="cust-fields cust-fields-3">
                <div className="cust-field">
                  <label className="cust-label">Máximo de parcelas</label>
                  <input
                    className="cust-input"
                    type="number"
                    name="maxTimes"
                    min={1}
                    max={12}
                    value={form.maxTimes}
                    onChange={handleChange}
                  />
                  <small style={{ color: "#888" }}>
                    Número máximo de vezes que o cliente pode parcelar
                  </small>
                </div>
                <div className="cust-field">
                  <label className="cust-label">Parcelas sem juros</label>
                  <input
                    className="cust-input"
                    type="number"
                    name="sellerTimes"
                    min={0}
                    max={form.maxTimes || 12}
                    value={form.sellerTimes}
                    onChange={handleChange}
                  />
                  <small style={{ color: "#888" }}>
                    Número de parcelas sem acréscimo de juros
                  </small>
                </div>
              </div>
              <div style={{ padding: "0 16px 16px", display: "flex", gap: 8 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                >
                  Salvar
                </Button>
                <Button variant="outlined" size="small" onClick={handleCancel}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
